import { useCallback, useMemo, useRef, useState } from "react";
import type {
    ExpandedState,
    RowSelectionState,
    Updater,
} from "@tanstack/react-table";
import { DirectorySort } from "@common/constants/counterparty";
import { noop } from "@common/helpers/function";
import { CounterpartyDirectoryView } from "@common/types/views/counterpartyDirectoryView";
import { View } from "@common/types/views/views";
import { GlDrawerState } from "@components/settings/Directory/Directory.context";
import { CounterpartyDirectoryViewRow } from "@components/settings/Directory/counterpartyDirectoryViewTypes";
import { DirectoryFilters } from "@components/settings/Directory/lib";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";

interface Params {
    view: CounterpartyDirectoryView;
    onViewChange: (view: View) => void;
    scope: DirectoryFilters;
    onScopeChange: (scope: DirectoryFilters) => void;
}

export function useCounterpartiesDirectoryContext({
    view,
    onViewChange,
    scope,
    onScopeChange,
}: Params) {
    const [counterpartySort, setCounterpartySort] = useState<DirectorySort>(
        DirectorySort.JOURNAL_ENTRY_LINES_DESC,
    );
    const [selectedRow, setSelectedRow] =
        useState<CounterpartyDirectoryViewRow | null>(null);
    const [selection, setSelection] = useState<RowSelectionState>({});
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [glDrawerState, setGlDrawerState] = useState<GlDrawerState | null>(
        null,
    );
    const tableRef = useRef<ViewTableHandle>(null);
    // Keeps the drawer content while it animates closed.
    const lastGlDrawerStateRef = useRef(glDrawerState);
    if (glDrawerState) {
        lastGlDrawerStateRef.current = glDrawerState;
    }

    const setRowSelection = useCallback(
        (updater: Updater<RowSelectionState>) => {
            setSelection((previous) =>
                typeof updater === "function" ? updater(previous) : updater,
            );
        },
        [],
    );
    const clearRowSelection = useCallback(() => setSelection({}), []);
    const resetRows = useCallback(() => {
        setExpanded({});
        setSelection({});
    }, []);
    const openGlDrawer = useCallback(
        (
            counterpartyId: string | null,
            isNoCounterparty: boolean,
            accountCode?: number,
        ) =>
            setGlDrawerState({ counterpartyId, isNoCounterparty, accountCode }),
        [],
    );
    const closeGlDrawer = useCallback(() => setGlDrawerState(null), []);
    const resetState = useCallback(() => {
        resetRows();
        setSelectedRow(null);
        setGlDrawerState(null);
    }, [resetRows]);

    const contextValue = useMemo(
        () => ({
            filters: scope,
            setFilters: onScopeChange,
            editedItem: undefined,
            setEditedItem: noop,
            addingNew: false,
            setAddingNew: noop,
            counterpartySort,
            setCounterpartySort,
            view,
            onViewChange,
            selectedRow,
            setSelectedRow,
            glDrawerState,
            openGlDrawer,
            closeGlDrawer,
            rowSelection: selection,
            setRowSelection,
            clearRowSelection,
        }),
        [
            scope,
            onScopeChange,
            counterpartySort,
            view,
            onViewChange,
            selectedRow,
            glDrawerState,
            openGlDrawer,
            closeGlDrawer,
            selection,
            setRowSelection,
            clearRowSelection,
        ],
    );

    return {
        contextValue,
        counterpartySort,
        expanded,
        setExpanded,
        resetRows,
        resetState,
        tableRef,
        glDrawerState,
        lastGlDrawerState: lastGlDrawerStateRef.current,
        closeGlDrawer,
    };
}
